import { useQuery } from '@apollo/client'
import { graphql } from '../../../gql'
import * as styled from '../../../util/styled'
import { useBrandId } from '../../../hooks/useGlobalRouting'
import { BrandSelectorView } from './BrandSelector/BrandSelector'
import { Mail, GraduationCap, BookOpen, Unlink, AtSign, Calendar } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useRouteMatchChecker, useRouteById } from '../../../hooks/useRouterData'
import { useFlagEnabled } from '../../../hooks/useFeatureFlag'
import { CampaignList } from './Campaign/CampaignList'

const TailwindScoped = styled.div('tailwind-scope')
const Wrapper = styled.div('flex flex-row h-full px-2 pt-2 pb-2 gap-5 w-[350px]')
const NavColumn = styled.div('flex flex-col gap-2 w-full pr-1 -ml-2 overflow-y-auto')
const SeparatorContainer = styled.div('h-full flex flex-col justify-center items-center')
const Separator = styled.div('h-full bg-border-standby w-[1px]')
const NavTitle = styled.div<{ loading: boolean}>((props) => [
  'px-3 text-brand text-4xl pt-2 font-semibold pb-2', props.loading && 'opacity-0'
])
const Section = styled.section<{ top?: boolean }>((props) => ['pb-2', !props.top && 'pt-2'])
const SectionLabel = styled.section<{}, { 'aria-label': string }>('font-semibold text-text-tertiary text-xs mx-4')
const NavItem = styled.component(Link)<{ active: boolean }>((props) => ['flex items-center gap-2 rounded-lg px-4 py-2 my-1 cursor-pointer text-brand font-semibold', {
  'bg-surface-darker text-brand': props.active,
  'hover:bg-surface-darker': !props.active
}])
const makeIcon = (icon: typeof Mail) => styled.component(icon)('w-4 h-4 text-brand')
const MailIcon = makeIcon(Mail)
const GraduationCapIcon = makeIcon(GraduationCap)
const BookIcon = makeIcon(BookOpen)
const UnlinkIcon = makeIcon(AtSign)
const BrokenLinkIcon = makeIcon(Unlink)
const CalendarIcon = makeIcon(Calendar)

const BrandInfoForSidebar = graphql(`
  query BrandInfoForSidebar($brandId: ID!) {
    brand(id: $brandId) {
      id
      title
    }
  }
`)

type BrandNameTitleProps = {
  brandId: string
}
const BrandNameTitle = (props: BrandNameTitleProps) => {
  const { brandId } = props
  const { data: data, loading } = useQuery(BrandInfoForSidebar, {
    variables: {
      brandId
    },
    fetchPolicy: 'cache-and-network'
  })

  return (
    <NavTitle loading={loading}>{data?.brand?.title ?? 'Loading'}</NavTitle>
  );
}


export const SideNavView = () => {
  const [brandId] = useBrandId()
  const isRoute = useRouteMatchChecker()
  const tutorialsRoute = useRouteById('tutorials')
  const lessonsRoute = useRouteById('lessons') 
  const emailInboxRoute = useRouteById('emailInbox')
  const unlinkedMentionsRoute = useRouteById('unlinkedMentions')
  const brokenLinkBuildingRoute = useRouteById('brokenLinkBuilding')
  const calendarRoute = useRouteById('calendar')
  const calendarEnabled = useFlagEnabled('calendar')
  
  return (
    <TailwindScoped>
      <Wrapper>
        <BrandSelectorView />
        <SeparatorContainer>
          <Separator/>
        </SeparatorContainer>
        <NavColumn>
          <Section top>
            { brandId && <BrandNameTitle brandId={brandId} /> }
            {!brandId && <NavTitle loading>Menu</NavTitle>}
            <NavItem
              active={isRoute('emailInbox')}
              to={emailInboxRoute.toLink({ brandId: brandId ?? '' })}
              aria-disabled={!brandId}
            >
              <MailIcon/>
              Emails
            </NavItem>
            {/* TODO: add brand settings */}
            {calendarEnabled && (
              <NavItem
                active={isRoute('calendar')}
                to={calendarRoute.toLink({ brandId: brandId ?? '' })}
                aria-disabled={!brandId}
              >
                <CalendarIcon />
                Calendar
              </NavItem>
            )}
          </Section>
          <CampaignList />
          <Section>
            <SectionLabel aria-label='SEO Tools'>SEO Tools</SectionLabel>
            <NavItem active={isRoute('unlinkedMentions')} to={unlinkedMentionsRoute.toLink({ brandId: brandId ?? '' })}>
              <UnlinkIcon />
              Unlinked Brand Mentions
            </NavItem>
            <NavItem active={isRoute('brokenLinkBuilding')} to={brokenLinkBuildingRoute.toLink({ brandId: brandId ?? '' })}>
              <BrokenLinkIcon />
              Broken Link Building
            </NavItem>
          </Section>
          <Section>
            <SectionLabel aria-label='Tutorials and Learning'>Tutorials and Learning</SectionLabel>
            <NavItem active={isRoute('tutorials')} to={tutorialsRoute.toLink({ brandId: brandId ?? '' })}>
              <BookIcon />
              Tutorials
            </NavItem>
            <NavItem active={isRoute('lessons')} to={lessonsRoute.toLink({ brandId: brandId ?? '' })}>
              <GraduationCapIcon />
              PR MasterClass
            </NavItem>
          </Section>
        </NavColumn>
      </Wrapper>
    </TailwindScoped>
  )
}